// Financial report — professional PDF-ready HTML that opens the print dialog.
// User can then "Save as PDF" in the browser print dialog.
import api from "./api";
import { fmtBRL, PAY_LABELS } from "./constants";

function esc(v) {
  if (v === null || v === undefined) return "";
  return String(v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function currentTimestamp() {
  return new Date().toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

export async function exportFinancialReport({
  periodLabel = "",
  orders = [],
  sales = [],
  expenses = [],
  totals = {},
  canSeeCost = true,
}) {
  // Load store info for the header
  let storeName = "Phone Lab";
  let storePhone = "";
  let storeAddress = "";
  try {
    const r = await api.get("/settings");
    storeName = r.data?.storeName || storeName;
    storePhone = r.data?.phone || "";
    storeAddress = r.data?.address || "";
  } catch { /* ignore */ }

  // Build a unified transaction list, sorted by date desc
  const transactions = [];
  orders.forEach((o) => {
    const disc = Number(o.discount) || 0;
    const gross = Number(o.total) || 0;
    const net = Math.max(0, gross - disc);
    transactions.push({
      date: o.exitDate || o.entryDate || "",
      type: "OS entregue",
      description: `${o.orderNumber || "OS"} · ${o.clientName || "—"} · ${o.model || ""}${o.services?.length ? " (" + o.services.join(", ") + ")" : ""}${disc > 0 ? ` · desc. R$ ${fmtBRL(disc)}` : ""}`,
      payMethod: PAY_LABELS[o.payMethod] || "—",
      inflow: net,
      outflow: canSeeCost ? (Number(o.costValue) || 0) : 0,
    });
  });
  sales.forEach((s) => {
    const disc = Number(s.discount) || 0;
    const gross = Number(s.value) || 0;
    const net = Math.max(0, gross - disc);
    transactions.push({
      date: s.date || "",
      type: "Venda de peça",
      description: `${s.piece || "Peça"} · ${s.clientStore || "—"}${disc > 0 ? ` · desc. R$ ${fmtBRL(disc)}` : ""}`,
      payMethod: PAY_LABELS[s.payMethod] || "—",
      inflow: net,
      outflow: canSeeCost ? (Number(s.costValue) || 0) : 0,
    });
  });
  expenses.forEach((e) => {
    transactions.push({
      date: e.dueDate || e.monthRef || "",
      type: e.recurring ? "Despesa fixa" : "Despesa",
      description: `${e.category || ""} — ${e.description || ""}`.replace(/^\s*—\s*|\s*—\s*$/g, ""),
      payMethod: "—",
      inflow: 0,
      outflow: Number(e.amount) || 0,
    });
  });

  // Sort by date desc (BR format dd/mm/yyyy or dd/mm)
  const dateKey = (br) => {
    if (!br) return 0;
    const parts = String(br).split("/");
    const d = parseInt(parts[0] || 0);
    const m = parseInt(parts[1] || 0);
    const y = parseInt(parts[2] || new Date().getFullYear());
    return y * 10000 + m * 100 + d;
  };
  transactions.sort((a, b) => dateKey(b.date) - dateKey(a.date));

  const receitaTotal = transactions.reduce((s, t) => s + t.inflow, 0);
  const despesaTotal = transactions.reduce((s, t) => s + t.outflow, 0);
  const saldo = receitaTotal - despesaTotal;

  const rowsHtml = transactions.map((t) => `
    <tr>
      <td class="date">${esc(t.date || "—")}</td>
      <td>
        <span class="type type-${t.type === "OS entregue" ? "os" : t.type === "Venda de peça" ? "sale" : "expense"}">${esc(t.type)}</span>
      </td>
      <td class="desc">${esc(t.description || "—")}</td>
      <td class="pay">${esc(t.payMethod)}</td>
      <td class="num inflow">${t.inflow > 0 ? "R$ " + fmtBRL(t.inflow) : "—"}</td>
      ${canSeeCost ? `<td class="num outflow">${t.outflow > 0 ? "R$ " + fmtBRL(t.outflow) : "—"}</td>` : ""}
    </tr>
  `).join("");

  const html = `
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Relatório Financeiro — ${esc(periodLabel)}</title>
        <style>
          * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          body {
            font-family: -apple-system, "Segoe UI", "Helvetica Neue", Arial, sans-serif;
            color: #0f172a;
            margin: 0;
            padding: 24px 30px;
            background: #f8fafc;
            font-size: 11pt;
          }
          .report {
            max-width: 900px; margin: 0 auto; background: #fff;
            border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden;
          }
          header {
            padding: 22px 28px;
            background: linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%);
            color: #fff;
          }
          header h1 { margin: 0; font-size: 20pt; font-weight: 800; letter-spacing: -0.02em; }
          header .brand { font-size: 10pt; font-weight: 600; opacity: 0.85; letter-spacing: 0.06em; text-transform: uppercase; margin-bottom: 4px; }
          header .meta { margin-top: 8px; font-size: 10pt; opacity: 0.9; display: flex; gap: 18px; flex-wrap: wrap; }
          header .meta b { font-weight: 600; }

          .kpis { display: grid; grid-template-columns: repeat(${canSeeCost ? 4 : 3}, 1fr); gap: 0; border-bottom: 1px solid #e2e8f0; }
          .kpi { padding: 18px 20px; border-right: 1px solid #e2e8f0; }
          .kpi:last-child { border-right: 0; }
          .kpi .lbl { font-size: 9pt; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; font-weight: 700; }
          .kpi .val { font-size: 18pt; font-weight: 800; margin-top: 4px; letter-spacing: -0.02em; font-variant-numeric: tabular-nums; }
          .kpi.receita .val { color: #1d4ed8; }
          .kpi.despesa .val { color: #b91c1c; }
          .kpi.custo .val { color: #b45309; }
          .kpi.saldo .val { color: #065f46; }
          .kpi.saldo.negative .val { color: #b91c1c; }

          section { padding: 20px 28px; }
          section h2 {
            font-size: 12pt; text-transform: uppercase; letter-spacing: 0.05em;
            color: #1e293b; margin: 0 0 12px; font-weight: 700;
            padding-bottom: 8px; border-bottom: 2px solid #e2e8f0;
          }

          table { width: 100%; border-collapse: collapse; }
          thead th {
            background: #f1f5f9; text-align: left; padding: 10px 12px;
            font-size: 9pt; font-weight: 700; color: #475569;
            text-transform: uppercase; letter-spacing: 0.04em;
            border-bottom: 1px solid #cbd5e1;
          }
          tbody td {
            padding: 8px 12px; border-bottom: 1px solid #f1f5f9;
            font-size: 10pt; vertical-align: top;
          }
          tbody tr:nth-child(even) td { background: #fafbfc; }
          td.date { white-space: nowrap; font-variant-numeric: tabular-nums; color: #475569; }
          td.desc { color: #1e293b; }
          td.pay { color: #64748b; font-size: 9.5pt; }
          td.num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; font-weight: 600; }
          td.inflow { color: #1d4ed8; }
          td.outflow { color: #b91c1c; }

          .type {
            display: inline-block; padding: 2px 8px; border-radius: 4px;
            font-size: 8.5pt; font-weight: 700; letter-spacing: 0.02em;
            white-space: nowrap;
          }
          .type-os { background: #dbeafe; color: #1e40af; }
          .type-sale { background: #f3e8ff; color: #6b21a8; }
          .type-expense { background: #fee2e2; color: #991b1b; }

          .empty {
            padding: 28px; text-align: center; color: #94a3b8; font-style: italic;
          }

          footer {
            padding: 14px 28px; background: #f8fafc; border-top: 1px solid #e2e8f0;
            font-size: 9pt; color: #64748b; display: flex; justify-content: space-between; align-items: center;
          }

          .actions {
            max-width: 900px; margin: 0 auto 20px; display: flex; justify-content: flex-end; gap: 8px;
          }
          .btn {
            padding: 9px 18px; border-radius: 6px; font-weight: 600; font-size: 10.5pt;
            border: 1px solid #cbd5e1; background: #fff; color: #334155; cursor: pointer;
          }
          .btn-primary { background: #1d4ed8; border-color: #1d4ed8; color: #fff; }
          .btn:hover { filter: brightness(0.96); }

          @media print {
            body { padding: 0; background: #fff; }
            .actions { display: none; }
            .report { border: 0; border-radius: 0; }
            @page { size: A4; margin: 12mm; }
          }
        </style>
      </head>
      <body>
        <div class="actions">
          <button class="btn" onclick="window.close()">Fechar</button>
          <button class="btn btn-primary" onclick="window.print()">🖨️ Imprimir / Salvar em PDF</button>
        </div>

        <div class="report">
          <header>
            <div class="brand">Relatório Financeiro</div>
            <h1>${esc(storeName)}</h1>
            <div class="meta">
              <span><b>Período:</b> ${esc(periodLabel)}</span>
              <span><b>Emitido em:</b> ${currentTimestamp()}</span>
              ${storePhone ? `<span><b>Contato:</b> ${esc(storePhone)}</span>` : ""}
              ${storeAddress ? `<span><b>Endereço:</b> ${esc(storeAddress)}</span>` : ""}
            </div>
          </header>

          <div class="kpis">
            <div class="kpi receita">
              <div class="lbl">Receita</div>
              <div class="val">R$ ${fmtBRL(receitaTotal)}</div>
            </div>
            <div class="kpi despesa">
              <div class="lbl">Despesas</div>
              <div class="val">R$ ${fmtBRL(despesaTotal - (canSeeCost ? (Number(totals.custoPeca) || 0) : 0))}</div>
            </div>
            ${canSeeCost ? `
            <div class="kpi custo">
              <div class="lbl">Custo de peças</div>
              <div class="val">R$ ${fmtBRL(Number(totals.custoPeca) || 0)}</div>
            </div>` : ""}
            <div class="kpi saldo ${saldo < 0 ? "negative" : ""}">
              <div class="lbl">${canSeeCost ? "Lucro líquido" : "Saldo"}</div>
              <div class="val">R$ ${fmtBRL(saldo)}</div>
            </div>
          </div>

          <section>
            <h2>Transações do período (${transactions.length})</h2>
            ${transactions.length === 0 ? `<div class="empty">Nenhuma transação registrada neste período.</div>` : `
            <table>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Tipo</th>
                  <th>Descrição</th>
                  <th>Pagamento</th>
                  <th style="text-align:right">Entrada</th>
                  ${canSeeCost ? "<th style='text-align:right'>Saída</th>" : ""}
                </tr>
              </thead>
              <tbody>${rowsHtml}</tbody>
              <tfoot>
                <tr>
                  <td colspan="4" style="text-align:right; font-weight:700; padding:12px; border-top:2px solid #cbd5e1;">TOTAIS</td>
                  <td class="num inflow" style="border-top:2px solid #cbd5e1; padding:12px;">R$ ${fmtBRL(receitaTotal)}</td>
                  ${canSeeCost ? `<td class="num outflow" style="border-top:2px solid #cbd5e1; padding:12px;">R$ ${fmtBRL(despesaTotal)}</td>` : ""}
                </tr>
              </tfoot>
            </table>
            `}
          </section>

          <footer>
            <span>Documento gerado automaticamente pelo sistema Phone Lab</span>
            <span>Página <span class="page-num"></span></span>
          </footer>
        </div>
      </body>
    </html>
  `;

  const win = window.open("", "_blank", "width=1000,height=800");
  if (!win) return;
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
}
